/**
 * ProfilePage
 *
 * User profile with natal chart info, language settings, and saved relationships.
 * Supports editing personal birth data and saved relationships.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import { getRelationships, deleteRelationship, updateRelationship } from '../api/relationships';
import { updatePrimaryChart } from '../api/charts';
import { EditBirthDataModal } from '../components/EditBirthDataModal';
import { EditRelationshipModal } from '../components/EditRelationshipModal';
import type { Relationship } from '../types';
import type { UpdateRelationshipData } from '../api/relationships';

function formatBirthDate(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, primaryChart, language, setLanguage, refreshPrimaryChart } = useAuth();
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [isLoadingRel, setIsLoadingRel] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Edit modals state
  const [isEditBirthDataOpen, setIsEditBirthDataOpen] = useState(false);
  const [isEditRelationshipOpen, setIsEditRelationshipOpen] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState<Relationship | null>(null);

  const loadRelationships = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingRel(true);
    try {
      const data = await getRelationships();
      setRelationships(data);
    } catch (error) {
      console.error('Failed to load relationships:', error);
    } finally {
      setIsLoadingRel(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadRelationships();
  }, [loadRelationships]);

  const handleDeleteRelationship = async (id: number, name: string) => {
    if (!confirm(t('profile.deletePersonConfirm', { name }))) return;

    setDeletingId(id);
    try {
      await deleteRelationship(id);
      setRelationships(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Failed to delete relationship:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditRelationship = (rel: Relationship) => {
    setEditingRelationship(rel);
    setIsEditRelationshipOpen(true);
  };

  const handleSaveRelationship = async (id: number, data: UpdateRelationshipData) => {
    const updated = await updateRelationship(id, data);
    setRelationships(prev => prev.map(r => r.id === id ? updated : r));
  };

  const handleSaveBirthData = async (data: { birth_date: string; birth_time: string; birth_place: string }) => {
    await updatePrimaryChart(data);
    // Refresh the primary chart in auth context
    if (refreshPrimaryChart) {
      await refreshPrimaryChart();
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value);
  };

  if (!isAuthenticated) {
    return (
      <div className="profile-page">
        <div className="empty-state">
          <h2>{t('auth.login')}</h2>
          <p>{t('profile.loginRequired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page" data-testid="profile-page">
      <h1>{t('profile.title')}</h1>

      {/* User Info Card */}
      <div className="profile-card">
        <div className="profile-header">
          {user?.picture && (
            <img
              src={user.picture}
              alt={user.name}
              className="profile-avatar"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="profile-info">
            <h2>{user?.name}</h2>
            <p>{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div className="profile-section">
        <h3>{t('profile.language')}</h3>
        <div className="profile-language-selector">
          <select
            value={language}
            onChange={handleLanguageChange}
            className="language-select-profile"
            data-testid="profile-language-selector"
          >
            <option value="sr">{t('settings.serbian')}</option>
            <option value="en">{t('settings.english')}</option>
          </select>
        </div>
      </div>

      {/* Birth Data Section */}
      <div className="profile-section">
        <div className="profile-section-header">
          <h3>{t('profile.birthData')}</h3>
          {primaryChart && (
            <button
              className="profile-edit-btn"
              onClick={() => setIsEditBirthDataOpen(true)}
              title={t('common.edit')}
            >
              {t('common.edit')}
            </button>
          )}
        </div>
        {primaryChart ? (
          <div className="profile-chart-card">
            <div className="chart-big-three">
              <div className="big-three-item">
                <span className="symbol">&#9737;</span>
                <span className="sign">{primaryChart.sun_sign}</span>
              </div>
              <div className="big-three-item">
                <span className="symbol">&#9789;</span>
                <span className="sign">{primaryChart.moon_sign}</span>
              </div>
              <div className="big-three-item">
                <span className="symbol">ASC</span>
                <span className="sign">{primaryChart.asc_sign}</span>
              </div>
            </div>
            <div className="chart-details">
              <p><strong>{t('profile.birthDateLabel')}:</strong> {formatBirthDate(primaryChart.birth_date)}</p>
              <p><strong>{t('profile.birthTimeLabel')}:</strong> {primaryChart.birth_time}</p>
              <p><strong>{t('profile.birthPlaceLabel')}:</strong> {primaryChart.birth_place}</p>
            </div>
          </div>
        ) : (
          <div className="profile-no-chart">
            <p>{t('profile.noChartYet')}</p>
            <button
              className="profile-action-btn"
              onClick={() => navigate('/')}
            >
              {t('profile.addBirthData')}
            </button>
          </div>
        )}
      </div>

      {/* Saved Relationships Section */}
      <div className="profile-section">
        <h3>{t('profile.savedPeople')}</h3>
        {isLoadingRel ? (
          <p className="loading-text">{t('common.loading')}</p>
        ) : relationships.length > 0 ? (
          <div className="relationships-list">
            {relationships.map(rel => (
              <div key={rel.id} className="relationship-item">
                <div className="relationship-info">
                  <span className="relationship-name">{rel.name}</span>
                  <span className="relationship-type">{rel.type_label}</span>
                  {rel.sun_sign && (
                    <span className="relationship-sign">&#9737; {rel.sun_sign}</span>
                  )}
                </div>
                <div className="relationship-actions">
                  <button
                    className="relationship-edit-btn"
                    onClick={() => handleEditRelationship(rel)}
                    title={t('common.edit')}
                  >
                    &#9998;
                  </button>
                  <button
                    className="relationship-delete-btn"
                    onClick={() => handleDeleteRelationship(rel.id, rel.name)}
                    disabled={deletingId === rel.id}
                    title={t('common.delete')}
                  >
                    {deletingId === rel.id ? '...' : '\u00D7'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="profile-no-relationships">
            <p>{t('profile.noSavedPeople')}</p>
            <button
              className="profile-action-btn"
              onClick={() => navigate('/')}
            >
              {t('profile.addPerson')}
            </button>
          </div>
        )}
      </div>

      {/* Edit Birth Data Modal */}
      <EditBirthDataModal
        isOpen={isEditBirthDataOpen}
        onClose={() => setIsEditBirthDataOpen(false)}
        onSave={handleSaveBirthData}
        currentData={primaryChart}
      />

      {/* Edit Relationship Modal */}
      <EditRelationshipModal
        isOpen={isEditRelationshipOpen}
        onClose={() => {
          setIsEditRelationshipOpen(false);
          setEditingRelationship(null);
        }}
        onSave={handleSaveRelationship}
        relationship={editingRelationship}
      />
    </div>
  );
}

export default ProfilePage;
